import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, MapPin, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useLang } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialProjects() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pr, cr] = await Promise.all([
          axios.get(`${API}/social-projects`),
          axios.get(`${API}/social-categories`),
        ]);
        setProjects(pr.data || []);
        setCategories(cr.data || []);
      } catch (e) {
        // keep empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const catLabel = (id) => {
    const c = categories.find(x => x.category_id === id);
    if (!c) return id;
    return lang === 'en' ? c.label_en : c.label_it;
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="social-projects-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-primary" data-testid="sp-title">{t('sp_page_title')}</h1>
          </div>
          <p className="text-base text-muted-foreground font-sans max-w-3xl">{t('sp_page_subtitle')}</p>
        </div>

        {loading && <div className="text-center py-16 text-muted-foreground font-sans">…</div>}

        {!loading && projects.length === 0 && (
          <Card className="rounded-2xl border-dashed border-2" data-testid="sp-empty">
            <CardContent className="p-10 text-center">
              <Heart className="w-10 h-10 text-primary/40 mx-auto mb-4" strokeWidth={1.2} />
              <p className="text-base text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
                {t('sp_empty_state')}
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sp-grid">
            {projects.map((p, i) => (
              <Link
                key={p.project_id}
                to={`/social-projects/${p.project_id}`}
                className="block"
                data-testid={`sp-card-${i}`}
              >
                <Card className="rounded-2xl border border-border hover:shadow-md transition-shadow h-full">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-serif font-bold text-primary mb-3" data-testid={`sp-card-title-${i}`}>{p.title}</h3>
                    <div className="space-y-1.5 text-sm font-sans mb-4">
                      <div className="flex items-center gap-2 text-foreground">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                        <span className="font-medium">{p.association_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span>{p.association_city}</span>
                      </div>
                    </div>
                    <p className="text-sm text-foreground/80 font-sans line-clamp-3 mb-4 min-h-[3.75rem]">{p.purpose}</p>
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs font-sans mb-1.5">
                        <span className="text-muted-foreground">{t('sp_card_progress')}</span>
                        <span className="font-semibold text-primary">{p.progress_percentage}%</span>
                      </div>
                      <Progress value={p.progress_percentage} className="h-2" />
                    </div>
                    {p.category_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {p.category_ids.slice(0, 3).map(cid => (
                          <Badge key={cid} variant="secondary" className="text-[10px] font-sans">{catLabel(cid)}</Badge>
                        ))}
                        {p.category_ids.length > 3 && (
                          <Badge variant="outline" className="text-[10px] font-sans">+{p.category_ids.length - 3}</Badge>
                        )}
                      </div>
                    )}
                    <div className="inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-primary">
                      {t('sp_detail_back').replace(/^.*ai /, '')} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
